import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  UserPlus,
  UserMinus,
  ArrowRightLeft,
  Plus,
  Settings,
  Eye,
  Edit,
  Trash2,
  LayoutGrid,
  ArrowLeft,
  FileText,
  Calculator,
  CheckCheck,
  Clock,
  Loader,
  XCircle,
  Printer,
  X,
} from 'lucide-react';
import StartOnboardingModal from './components/StartOnboardingModal';
import StartOffboardingModal from './components/StartOffboardingModal';
import InitiateMovementModal from './components/InitiateMovementModal';
import EditOnboardingModal from './components/EditOnboardingModal';
import EditOffboardingModal from './components/EditOffboardingModal';
import EditMovementModal from './components/EditMovementModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import ConfirmationModal from './components/ConfirmationModal';
import OnboardingDetailsModal from './components/OnboardingDetailsModal';
import OffboardingDetailsModal from './components/OffboardingDetailsModal';
import MovementDetailsModal from './components/MovementDetailsModal';
import TemplateManagerPanel from './components/TemplateManagerPanel';
import {
  type OnboardingProcessView,
  OnboardingStatus,
  ProcessTaskStatus,
  TemplateCategory,
  type LifecycleTemplateView,
  type OffboardingProcessView,
  OffboardingStatus,
  type InternalMovementView,
  MovementStatus,
  ApprovalStatus,
} from '../../models/employee';
import { useToast, ToastRenderer } from '../../hooks/useToast';
import { getOnboardingProcesses, deleteOnboarding, cancelOnboarding } from '../../api/Employee/onboardingProcesses';
import { getOffboardingProcesses, deleteOffboarding, cancelOffboarding, calculateEos } from '../../api/Employee/offboardingProcesses';
import { getInternalMovements, deleteMovement, cancelMovement, applyMovement } from '../../api/Employee/internalMovements';
import { getTemplates } from '../../api/Employee/lifecycleTemplates';
import { appDate } from '../../utils/appDateFormat';
import { apiDateOnlyParam } from '@/utils/apiDate';

// ---- Types for Kanban (uses simpler shape) ----
interface OnboardingTask {
  id: string;
  name: string;
  type: string;
  status: string;
  dueDate: string;
  mandatory: boolean;
}

interface TabDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  count: number;
}


// ---- Helpers ----
const getStatusColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'in progress':
      return 'bg-blue-100 text-blue-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'not started':
      return 'bg-gray-100 text-gray-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getOnboardingStatusLabel = (status: OnboardingStatus): string => {
  switch (status) {
    case OnboardingStatus.NotStarted: return 'Not Started';
    case OnboardingStatus.InProgress: return 'In Progress';
    case OnboardingStatus.Completed: return 'Completed';
    case OnboardingStatus.Cancelled: return 'Cancelled';
    default: return 'Unknown';
  }
};

const getMovementStatusLabel = (status: MovementStatus): string => {
  switch (status) {
    case MovementStatus.NotStarted: return 'Not Started';
    case MovementStatus.InProgress: return 'In Progress';
    case MovementStatus.Completed: return 'Completed';
    case MovementStatus.Cancelled: return 'Cancelled';
    default: return 'Unknown';
  }
};


// A movement can only be applied once the approval chain reaches Approved (the server rejects
// anything else). Returns the label for the stage the request is still sitting at, or null when it
// is no longer waiting on anyone — so the action row can explain the missing "Apply Changes" button
// instead of leaving a gap. Rejected is not a waiting state: rejection also cancels the movement,
// which already hides the whole action set.
const getPendingApprovalLabel = (status: ApprovalStatus): string | null => {
  switch (status) {
    case ApprovalStatus.PendingManagerApproval: return 'Awaiting manager approval';
    // The stage between the two: the RECEIVING manager has to accept the employee. Worded as
    // "acceptance" rather than a second "approval" so the two manager stages are tellable apart
    // in the list — otherwise a movement looks like it never moved.
    case ApprovalStatus.PendingReceivingManagerApproval: return 'Awaiting receiving manager acceptance';
    case ApprovalStatus.PendingHrApproval: return 'Awaiting HR approval';
    default: return null;
  }
};

const getOffboardingStatusLabel = (status: OffboardingStatus): string => {
  switch (status) {
    case OffboardingStatus.NotStarted: return 'Not Started';
    case OffboardingStatus.InProgress: return 'In Progress';
    case OffboardingStatus.Completed: return 'Completed';
    case OffboardingStatus.Cancelled: return 'Cancelled';
    default: return 'Unknown';
  }
};

const mapOffboardingToKanbanTasks = (process: OffboardingProcessView): OnboardingTask[] => {
  return (process.tasks || []).map(task => ({
    id: String(task.id),
    name: task.name,
    type: task.typeName || '',
    status: getTaskStatusLabel(task.status),
    dueDate: apiDateOnlyParam(task.dueDate),
    mandatory: task.mandatory,
  }));
};

const getTaskStatusLabel = (status: ProcessTaskStatus): string => {
  switch (status) {
    case ProcessTaskStatus.Pending: return 'Pending';
    case ProcessTaskStatus.InProgress: return 'In Progress';
    case ProcessTaskStatus.Completed: return 'Completed';
    case ProcessTaskStatus.Cancelled: return 'Cancelled';
    default: return 'Unknown';
  }
};

const mapProcessToKanbanTasks = (process: OnboardingProcessView): OnboardingTask[] => {
  return (process.tasks || []).map(task => ({
    id: String(task.id),
    name: task.name,
    type: task.typeName || '',
    status: getTaskStatusLabel(task.status),
    dueDate: apiDateOnlyParam(task.dueDate),
    mandatory: task.mandatory,
  }));
};

const mapMovementToKanbanTasks = (movement: InternalMovementView): OnboardingTask[] => {
  return (movement.tasks || []).map(task => ({
    id: String(task.id),
    name: task.name,
    type: task.typeName || '',
    status: getTaskStatusLabel(task.status),
    dueDate: apiDateOnlyParam(task.dueDate),
    mandatory: task.mandatory,
  }));
};

// ---- Kanban View (inline) ----
function KanbanBoard({ tasks, t }: { tasks: OnboardingTask[]; t: (k: string) => string }) {
  const columns = [
    { key: 'Pending', label: 'Pending', color: 'bg-yellow-500' },
    { key: 'In Progress', label: 'In Progress', color: 'bg-blue-500' },
    { key: 'Completed', label: 'Completed', color: 'bg-green-500' },
    { key: 'Cancelled', label: 'Cancelled', color: 'bg-red-500' },
  ];
  return (
    <div className="grid grid-cols-4 gap-4">
      {columns.map((col) => {
        const colTasks = tasks.filter(
          (tk) => tk.status === col.key || (col.key === 'Pending' && tk.status === 'Scheduled')
        );
        return (
          <div key={col.key} className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-3 h-3 rounded-full ${col.color}`} />
              <h4 className="text-sm font-semibold text-gray-700">
                {t(col.label)} ({colTasks.length})
              </h4>
            </div>
            <div className="space-y-2">
              {colTasks.map((task) => (
                <div
                  key={task.id}
                  className="bg-card rounded-md border border-border p-3 shadow-sm"
                >
                  <p className="text-sm font-medium text-gray-900">{task.name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">{task.type}</span>
                    {task.mandatory && (
                      <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                        {t('Required')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-700 mt-1">{t('Due')}: {appDate(task.dueDate)}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Main Component ----
export default function EmploymentLifecycle() {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const validTabs = ['onboarding', 'offboarding', 'internal-movements'];
  const initialTab = tabParam && validTabs.includes(tabParam) ? tabParam : 'onboarding';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [onboardingViewType, setOnboardingViewType] = useState<'list' | 'kanban'>('list');
  const [offboardingViewType, setOffboardingViewType] = useState<'list' | 'kanban'>('list');
  const [onboardingKanbanProcess, setOnboardingKanbanProcess] = useState<{ tasks: OnboardingTask[] } | null>(null);
  const [offboardingKanbanProcess, setOffboardingKanbanProcess] = useState<{ tasks: OnboardingTask[] } | null>(null);
  const [movementViewType, setMovementViewType] = useState<'list' | 'kanban'>('list');
  const [movementKanbanProcess, setMovementKanbanProcess] = useState<{ tasks: OnboardingTask[] } | null>(null);

  // Onboarding state (real data)
  const [onboardingProcesses, setOnboardingProcesses] = useState<OnboardingProcessView[]>([]);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const [onboardingTemplates, setOnboardingTemplates] = useState<LifecycleTemplateView[]>([]);

  // Offboarding state (real data)
  const [offboardingProcesses, setOffboardingProcesses] = useState<OffboardingProcessView[]>([]);
  const [offboardingLoading, setOffboardingLoading] = useState(false);
  const [offboardingError, setOffboardingError] = useState<string | null>(null);
  const [offboardingTemplates, setOffboardingTemplates] = useState<LifecycleTemplateView[]>([]);

  // Movements state (real data)
  const [internalMovements, setInternalMovements] = useState<InternalMovementView[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementsError, setMovementsError] = useState<string | null>(null);
  const [movementTemplates, setMovementTemplates] = useState<LifecycleTemplateView[]>([]);

  // Template state
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [templateCategory, setTemplateCategory] = useState<0 | 1 | 2>(0); // 0=Onboarding, 1=Offboarding, 2=Movement

  // Single failure surface for the row actions. They previously either swallowed a rejected result
  // (the confirmation modal closed, the list never changed, the user was told nothing) or threw a
  // raw browser alert(). Both are dead ends: the server still refuses these calls on authority and
  // on state, so every one of them needs an answer the user can read and act on.
  const runLifecycleAction = useCallback(async (
    action: () => Promise<{ errors?: { description?: string }[] }>,
    opts: {
      failureMessage: string;
      successMessage?: string;
      onSuccess?: () => void;
      onSettled?: () => void;
    },
  ) => {
    try {
      const result = await action();
      if (result.errors && result.errors.length > 0) {
        showToast(result.errors[0]?.description || opts.failureMessage, 'error');
      } else {
        if (opts.successMessage) showToast(opts.successMessage, 'success');
        opts.onSuccess?.();
      }
    } catch (err) {
      const description = (err as { errors?: { description?: string }[] })?.errors?.[0]?.description;
      showToast(description || opts.failureMessage, 'error');
    } finally {
      opts.onSettled?.();
    }
  }, [showToast]);

  const refreshMovements = useCallback(async () => {
    setMovementsLoading(true);
    setMovementsError(null);
    try {
      const result = await getInternalMovements();
      if (result.errors && result.errors.length > 0) {
        setMovementsError(result.errors[0]?.description || 'Failed to load movements');
      } else {
        setInternalMovements(result.payload || []);
      }
    } catch {
      setMovementsError('Failed to load movements');
    } finally {
      setMovementsLoading(false);
    }
  }, []);

  const loadMovementTemplates = useCallback(async () => {
    try {
      const result = await getTemplates(TemplateCategory.Movement);
      if (!result.errors || result.errors.length === 0) {
        setMovementTemplates(result.payload || []);
      }
    } catch {
      // silently fail - templates are optional
    }
  }, []);

  const refreshOffboarding = useCallback(async () => {
    setOffboardingLoading(true);
    setOffboardingError(null);
    try {
      const result = await getOffboardingProcesses();
      if (result.errors && result.errors.length > 0) {
        setOffboardingError(result.errors[0]?.description || 'Failed to load offboarding processes');
      } else {
        setOffboardingProcesses(result.payload || []);
      }
    } catch {
      setOffboardingError('Failed to load offboarding processes');
    } finally {
      setOffboardingLoading(false);
    }
  }, []);

  const loadOffboardingTemplates = useCallback(async () => {
    try {
      const result = await getTemplates(TemplateCategory.Offboarding);
      if (!result.errors || result.errors.length === 0) {
        setOffboardingTemplates(result.payload || []);
      }
    } catch {
      // silently fail - templates are optional
    }
  }, []);

  const refreshOnboarding = useCallback(async () => {
    setOnboardingLoading(true);
    setOnboardingError(null);
    try {
      const result = await getOnboardingProcesses();
      if (result.errors && result.errors.length > 0) {
        setOnboardingError(result.errors[0]?.description || 'Failed to load onboarding processes');
      } else {
        setOnboardingProcesses(result.payload || []);
      }
    } catch {
      setOnboardingError('Failed to load onboarding processes');
    } finally {
      setOnboardingLoading(false);
    }
  }, []);

  const loadOnboardingTemplates = useCallback(async () => {
    try {
      const result = await getTemplates(TemplateCategory.Onboarding);
      if (!result.errors || result.errors.length === 0) {
        setOnboardingTemplates(result.payload || []);
      }
    } catch {
      // silently fail - templates are optional
    }
  }, []);

  useEffect(() => {
    refreshOnboarding();
    loadOnboardingTemplates();
    refreshOffboarding();
    loadOffboardingTemplates();
    refreshMovements();
    loadMovementTemplates();
  }, [refreshOnboarding, loadOnboardingTemplates, refreshOffboarding, loadOffboardingTemplates, refreshMovements, loadMovementTemplates]);

  // Sync active tab from URL query param (e.g. ?tab=offboarding from notification click)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Template handlers
  const openTemplateManager = (type: 'onboarding' | 'offboarding' | 'movement') => {
    setTemplateCategory(type === 'onboarding' ? 0 : type === 'offboarding' ? 1 : 2);
    setShowTemplateManager(true);
  };

  // Modal state
  const [showStartOnboarding, setShowStartOnboarding] = useState(false);
  const [showStartOffboarding, setShowStartOffboarding] = useState(false);
  const [showInitiateMovement, setShowInitiateMovement] = useState(false);
  const [showEditOnboarding, setShowEditOnboarding] = useState(false);
  const [showEditOffboarding, setShowEditOffboarding] = useState(false);
  const [showEditMovement, setShowEditMovement] = useState(false);
  const [showViewOnboarding, setShowViewOnboarding] = useState(false);
  const [showViewOffboarding, setShowViewOffboarding] = useState(false);
  const [showViewMovement, setShowViewMovement] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<OnboardingProcessView | OffboardingProcessView | null>(null);
  const [selectedMovement, setSelectedMovement] = useState<InternalMovementView | null>(null);
  const [deleteConfig, setDeleteConfig] = useState<any>({});
  const [confirmationConfig, setConfirmationConfig] = useState<any>({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportHtml, setReportHtml] = useState('');
  const reportIframeRef = useRef<HTMLIFrameElement>(null);

  const tabs: TabDef[] = [
    { id: 'onboarding', label: 'Onboarding', icon: <UserPlus className="w-4 h-4" />, count: onboardingProcesses.length },
    { id: 'internal-movements', label: 'Internal Movements', icon: <ArrowRightLeft className="w-4 h-4" />, count: internalMovements.length },
    { id: 'offboarding', label: 'Offboarding', icon: <UserMinus className="w-4 h-4" />, count: offboardingProcesses.length },
  ];

  // ---- Generate Offboarding Report ----
  const generateOffboardingReport = (process: OffboardingProcessView) => {
    const completedTasks = (process.tasks || []).filter(t => t.status === ProcessTaskStatus.Completed).length;
    const totalTasks = (process.tasks || []).length;
    const currency = process.currencyCode || 'AED';

    const taskRows = (process.tasks || []).map(task => {
      const statusLabel = task.status === ProcessTaskStatus.Completed ? t('Completed')
        : task.status === ProcessTaskStatus.InProgress ? t('In Progress')
        : task.status === ProcessTaskStatus.Cancelled ? t('Cancelled')
        : t('Pending');
      return `<tr>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${task.name}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${task.typeName || '-'}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${statusLabel}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${task.dueDate ? appDate(task.dueDate) : '-'}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html dir="ltr">
<head>
  <meta charset="utf-8">
  <title>${t('Offboarding Report')} - ${process.employeeName}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 40px; color: #1f2937; }
    h1 { font-size: 24px; margin-bottom: 4px; }
    h2 { font-size: 18px; margin-top: 24px; margin-bottom: 12px; border-bottom: 2px solid #e5e7eb; padding-bottom: 6px; }
    .subtitle { color: #6b7280; font-size: 14px; margin-bottom: 24px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 24px; }
    .info-item { padding: 8px 0; }
    .info-label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
    .info-value { font-size: 14px; font-weight: 600; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; font-size: 14px; }
    th { text-align: left; padding: 8px 12px; background: #f9fafb; border-bottom: 2px solid #e5e7eb; font-weight: 600; font-size: 12px; text-transform: uppercase; color: #6b7280; }
    .summary-box { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 16px; margin: 16px 0; }
    .eos-box { background: #fefce8; border: 1px solid #fde68a; border-radius: 8px; padding: 16px; margin: 16px 0; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #9ca3af; }
    @media print { body { margin: 20px; } }
  </style>
</head>
<body>
  <h1>${t('Offboarding Report')}</h1>
  <p class="subtitle">${t('Generated on')} ${appDate(new Date())}</p>

  <h2>${t('Employee Details')}</h2>
  <div class="info-grid">
    <div class="info-item">
      <div class="info-label">${t('Employee Name')}</div>
      <div class="info-value">${process.employeeName || '-'}</div>
    </div>
    <div class="info-item">
      <div class="info-label">${t('Position')}</div>
      <div class="info-value">${process.position || '-'}</div>
    </div>
    <div class="info-item">
      <div class="info-label">${t('Department')}</div>
      <div class="info-value">${process.department || '-'}</div>
    </div>
    <div class="info-item">
      <div class="info-label">${t('Manager')}</div>
      <div class="info-value">${process.managerName || '-'}</div>
    </div>
    <div class="info-item">
      <div class="info-label">${t('Last Working Day')}</div>
      <div class="info-value">${process.lastWorkingDay ? appDate(process.lastWorkingDay) : '-'}</div>
    </div>
    <div class="info-item">
      <div class="info-label">${t('Exit Reason')}</div>
      <div class="info-value">${process.exitReasonName || '-'}</div>
    </div>
  </div>

  <div class="summary-box">
    <strong>${t('Task Completion')}:</strong> ${completedTasks} / ${totalTasks} ${t('tasks completed')} (${totalTasks > 0 ? Math.round(process.completionPercentage) : 0}%)
  </div>

  ${process.eosAmount != null ? `
  <div class="eos-box">
    <strong>${t('End of Service Amount')}:</strong> ${currency} ${process.eosAmount.toLocaleString()}
  </div>
  ` : ''}

  <h2>${t('Task List')}</h2>
  <table>
    <thead>
      <tr>
        <th>${t('Task')}</th>
        <th>${t('Type')}</th>
        <th>${t('Status')}</th>
        <th>${t('Due Date')}</th>
      </tr>
    </thead>
    <tbody>
      ${taskRows || `<tr><td colspan="4" style="padding:16px;text-align:center;color:#9ca3af;">${t('No tasks')}</td></tr>`}
    </tbody>
  </table>

  <div class="footer">
    ${t('This report was generated automatically by the Mowazaf HR System.')}
  </div>
</body>
</html>`;

    setReportHtml(html);
    setShowReportModal(true);
  };

  // ---- Onboarding Tab ----
  const renderOnboardingTab = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('Employee Onboarding')}</h3>
          <p className="text-sm text-gray-600">{t('Track new employee integration and setup processes')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            className="inline-flex items-center px-3 py-2 border border-border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => openTemplateManager('onboarding')}
          >
            <Settings className="w-4 h-4 me-2" />
            {t('Manage Templates')}
          </button>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600"
            onClick={() => setShowStartOnboarding(true)}
          >
            <Plus className="w-4 h-4 me-2" />
            {t('Start New Onboarding')}
          </button>
        </div>
      </div>

      {/* Loading / Error / Empty States */}
      {onboardingLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-6 h-6 animate-spin text-blue-600 me-2" />
          <span className="text-gray-600">{t('Loading onboarding processes...')}</span>
        </div>
      )}

      {onboardingError && !onboardingLoading && (
        <div className="text-center py-12">
          <p className="text-red-600 mb-2">{onboardingError}</p>
          <button onClick={refreshOnboarding} className="text-blue-600 hover:underline text-sm">{t('Retry')}</button>
        </div>
      )}

      {!onboardingLoading && !onboardingError && onboardingProcesses.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t('No onboarding processes yet. Click "Start New Onboarding" to begin.')}</p>
        </div>
      )}

      {/* List View */}
      {!onboardingLoading && !onboardingError && onboardingViewType === 'list' && onboardingProcesses.length > 0 && (
        <div className="space-y-6">
          {onboardingProcesses.map((process) => {
            const statusLabel = getOnboardingStatusLabel(process.status);
            return (
            <div key={process.id} className="bg-card border border-border rounded-lg p-6 hover:border-border transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{process.employeeName}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(statusLabel)}`}>
                      {t(statusLabel)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">{t('Position')}:</span>
                      <div className="font-medium">{process.position || t('N/A')}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('Department')}:</span>
                      <div className="font-medium">{process.department}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('Start Date')}:</span>
                      <div className="font-medium">{appDate(process.startDate)}</div>
                    </div>
                  </div>
                </div>
                <div className="text-end ms-6">
                  <div className="text-sm text-gray-500 mb-1">{t('Progress')}</div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${process.completionPercentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{Math.round(process.completionPercentage)}%</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center space-x-3">
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => { setOnboardingKanbanProcess({ tasks: mapProcessToKanbanTasks(process) }); setOnboardingViewType('kanban'); }}
                  >
                    <LayoutGrid className="w-3.5 h-3.5 me-1" />
                    {t('Kanban')}
                  </button>
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => { setSelectedProcess(process); setShowViewOnboarding(true); }}
                  >
                    <Eye className="w-3.5 h-3.5 me-1" />
                    {t('View Details')}
                  </button>
                  {process.status !== OnboardingStatus.Cancelled && process.status !== OnboardingStatus.Completed && (
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                      onClick={() => { setSelectedProcess(process); setShowEditOnboarding(true); }}
                    >
                      <Edit className="w-3.5 h-3.5 me-1" />
                      {t('Edit')}
                    </button>
                  )}
                  {process.status !== OnboardingStatus.Cancelled && process.status !== OnboardingStatus.Completed && (
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-orange-700 text-sm font-medium border border-orange-300 rounded-md"
                      onClick={() => {
                        setConfirmationConfig({
                          title: t('Cancel Onboarding Process'),
                          message: t('Are you sure you want to cancel the onboarding process for') + ` ${process.employeeName}?`,
                          confirmText: t('Cancel Process'),
                          onConfirm: () => runLifecycleAction(
                            () => cancelOnboarding(process.id),
                            {
                              failureMessage: t('Failed to cancel onboarding process'),
                              onSuccess: refreshOnboarding,
                              onSettled: () => setShowConfirmation(false),
                            },
                          )
                        });
                        setShowConfirmation(true);
                      }}
                    >
                      <XCircle className="w-3.5 h-3.5 me-1" />
                      {t('Cancel')}
                    </button>
                  )}
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => {
                      setDeleteConfig({
                        title: t('Delete Onboarding Process'),
                        message: t('Are you sure you want to delete the onboarding process for') + ` ${process.employeeName}?`,
                        confirmText: t('Delete Process'),
                        onConfirm: () => runLifecycleAction(
                          () => deleteOnboarding(process.id),
                          {
                            failureMessage: t('Failed to delete onboarding process'),
                            onSuccess: refreshOnboarding,
                            onSettled: () => setShowDeleteConfirmation(false),
                          },
                        )
                      });
                      setShowDeleteConfirmation(true);
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5 me-1" />
                    {t('Delete')}
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Kanban View */}
      {onboardingViewType === 'kanban' && onboardingKanbanProcess && (
        <div>
          <div className="mb-4">
            <button
              onClick={() => { setOnboardingViewType('list'); setOnboardingKanbanProcess(null); }}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-border rounded-md hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 me-2" />
              {t('Back to List View')}
            </button>
          </div>
          <KanbanBoard tasks={onboardingKanbanProcess.tasks} t={t} />
        </div>
      )}
    </div>
  );

  // ---- Internal Movements Tab ----
  const renderInternalMovementsTab = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('Internal Movements')}</h3>
          <p className="text-sm text-gray-600">{t('Manage promotions, transfers, and contract changes')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            className="inline-flex items-center px-3 py-2 border border-border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => openTemplateManager('movement')}
          >
            <Settings className="w-4 h-4 me-2" />
            {t('Manage Templates')}
          </button>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600"
            onClick={() => setShowInitiateMovement(true)}
          >
            <Plus className="w-4 h-4 me-2" />
            {t('Initiate Movement')}
          </button>
        </div>
      </div>

      {/* Loading / Error / Empty States */}
      {movementsLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-6 h-6 animate-spin text-blue-600 me-2" />
          <span className="text-gray-600">{t('Loading movements...')}</span>
        </div>
      )}

      {movementsError && !movementsLoading && (
        <div className="text-center py-12">
          <p className="text-red-600 mb-2">{movementsError}</p>
          <button onClick={refreshMovements} className="text-blue-600 hover:underline text-sm">{t('Retry')}</button>
        </div>
      )}

      {!movementsLoading && !movementsError && internalMovements.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t('No internal movements yet. Click "Initiate Movement" to begin.')}</p>
        </div>
      )}

      {/* List View */}
      {!movementsLoading && !movementsError && internalMovements.length > 0 && movementViewType === 'list' && (
        <div className="space-y-4">
          {internalMovements.map((movement) => {
            const isSettled = movement.status === MovementStatus.Completed || movement.status === MovementStatus.Cancelled;
            const pendingApprovalLabel = getPendingApprovalLabel(movement.approvalStatus);
            return (
            <div key={movement.id} className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-base font-semibold text-gray-900">{movement.employeeName}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(getMovementStatusLabel(movement.status))}`}>
                      {t(getMovementStatusLabel(movement.status))}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {movement.movementTypeName || t('Unknown')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">{t('Position')}:</span>
                      <div className="font-medium">
                        <span className="text-gray-400 line-through">{movement.currentPosition || t('N/A')}</span>
                        {' → '}
                        <span className="text-gray-900">{movement.newPosition || t('N/A')}</span>
                      </div>
                    </div>
                    {movement.currentDepartmentName !== movement.newDepartmentName && (
                      <div>
                        <span className="text-gray-500">{t('Department')}:</span>
                        <div className="font-medium">
                          <span className="text-gray-400">{movement.currentDepartmentName || '-'}</span>
                          {' → '}
                          <span className="text-gray-900">{movement.newDepartmentName || '-'}</span>
                        </div>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">{t('Effective Date')}:</span>
                      <div className="font-medium">{appDate(movement.effectiveDate)}</div>
                    </div>
                  </div>
                </div>
                <div className="text-end ms-6">
                  <div className="text-sm text-gray-500 mb-1">{t('Progress')}</div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${movement.completionPercentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{Math.round(movement.completionPercentage)}%</span>
                  </div>
                </div>
              </div>

              {/* A rejection carries a MANDATORY explanation. Without this the card says only
                  "Cancelled" and the reason — already captured and stored — is unreadable anywhere
                  in the product. Same treatment as a rejected leave: rejecter + reason, in place. */}
              {(movement.rejectionReason || movement.rejectedByName) && (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                  <div className="text-xs font-medium text-red-800">{t('Rejection Reason')}</div>
                  {movement.rejectionReason && (
                    <div className="text-sm text-red-700 mt-0.5 whitespace-pre-line">{movement.rejectionReason}</div>
                  )}
                  {movement.rejectedByName && (
                    <div className="text-xs text-red-700 mt-1">
                      {t('Reviewed by')}: <span className="font-medium">{movement.rejectedByName}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between pt-4 border-t border-border">
                {/* gap, not space-x: space-x-* emits a physical margin that does not flip in RTL, and
                    this row now carries enough actions to wrap on narrow viewports. */}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => { setMovementKanbanProcess({ tasks: mapMovementToKanbanTasks(movement) }); setMovementViewType('kanban'); }}
                  >
                    <LayoutGrid className="w-3.5 h-3.5 me-1" />
                    {t('Kanban')}
                  </button>
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => { setSelectedMovement(movement); setShowViewMovement(true); }}
                  >
                    <Eye className="w-3.5 h-3.5 me-1" />
                    {t('View Details')}
                  </button>
                  {!isSettled && (
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                      onClick={() => { setSelectedMovement(movement); setShowEditMovement(true); }}
                    >
                      <Edit className="w-3.5 h-3.5 me-1" />
                      {t('Edit')}
                    </button>
                  )}
                  {/* Apply is only offered once the approval chain has cleared. The server refuses to
                      apply anything that is not Approved, so rendering the button on a pending
                      movement only walks the user into a rejection. */}
                  {!isSettled && movement.approvalStatus === ApprovalStatus.Approved && new Date(movement.effectiveDate) <= new Date() && (
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-green-700 text-sm font-medium border border-green-300 rounded-md hover:bg-green-50"
                      onClick={() => {
                        setConfirmationConfig({
                          title: t('Apply Movement Changes'),
                          message: t('This will update') + ` ${movement.employeeName}` + t("'s department and position immediately. Continue?"),
                          confirmText: t('Apply Changes'),
                          onConfirm: () => runLifecycleAction(
                            () => applyMovement(movement.id),
                            {
                              successMessage: t('Movement changes applied'),
                              failureMessage: t('Failed to apply movement'),
                              onSuccess: refreshMovements,
                              onSettled: () => setShowConfirmation(false),
                            },
                          ),
                        });
                        setShowConfirmation(true);
                      }}
                    >
                      <CheckCheck className="w-3.5 h-3.5 me-1" />
                      {t('Apply Changes')}
                    </button>
                  )}
                  {/* Takes the slot Apply will occupy, so the absent action reads as "waiting on
                      someone" rather than as nothing to do here. Deliberately not a button. */}
                  {!isSettled && pendingApprovalLabel && (
                    <span className="inline-flex items-center py-1.5 text-amber-700 text-sm font-medium">
                      <Clock className="w-3.5 h-3.5 me-1.5" aria-hidden="true" />
                      {t(pendingApprovalLabel)}
                    </span>
                  )}
                  {/* The other reason Apply is absent: the chain has cleared but the movement is not
                      due yet. Same slot, same inline-status treatment — never a button. */}
                  {!isSettled
                    && movement.approvalStatus === ApprovalStatus.Approved
                    && new Date(movement.effectiveDate) > new Date() && (
                    <span className="inline-flex items-center py-1.5 text-amber-700 text-sm font-medium">
                      <Clock className="w-3.5 h-3.5 me-1.5" aria-hidden="true" />
                      {t('Applies on {{date}}', { date: appDate(movement.effectiveDate) })}
                    </span>
                  )}
                  {!isSettled && (
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-orange-700 text-sm font-medium border border-orange-300 rounded-md"
                      onClick={() => {
                        setConfirmationConfig({
                          title: t('Cancel Movement'),
                          message: t('Are you sure you want to cancel this movement for') + ` ${movement.employeeName}?`,
                          confirmText: t('Cancel Movement'),
                          onConfirm: () => runLifecycleAction(
                            () => cancelMovement(movement.id),
                            {
                              failureMessage: t('Failed to cancel movement'),
                              onSuccess: refreshMovements,
                              onSettled: () => setShowConfirmation(false),
                            },
                          ),
                        });
                        setShowConfirmation(true);
                      }}
                    >
                      <XCircle className="w-3.5 h-3.5 me-1" />
                      {t('Cancel')}
                    </button>
                  )}
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => {
                      setDeleteConfig({
                        title: t('Delete Internal Movement'),
                        message: t('Are you sure you want to delete this movement for') + ` ${movement.employeeName}?`,
                        confirmText: t('Delete Movement'),
                        onConfirm: () => runLifecycleAction(
                          () => deleteMovement(movement.id),
                          {
                            failureMessage: t('Failed to delete movement'),
                            onSuccess: refreshMovements,
                            onSettled: () => setShowDeleteConfirmation(false),
                          },
                        ),
                      });
                      setShowDeleteConfirmation(true);
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5 me-1" />
                    {t('Delete')}
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Kanban View */}
      {movementViewType === 'kanban' && movementKanbanProcess && (
        <div>
          <div className="mb-4">
            <button
              onClick={() => { setMovementViewType('list'); setMovementKanbanProcess(null); }}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-border rounded-md hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 me-2" />
              {t('Back to List View')}
            </button>
          </div>
          <KanbanBoard tasks={movementKanbanProcess.tasks} t={t} />
        </div>
      )}
    </div>
  );

  // ---- Offboarding Tab ----
  const renderOffboardingTab = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('Employee Offboarding')}</h3>
          <p className="text-sm text-gray-600">{t('Manage employee exits and final settlements')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            className="inline-flex items-center px-3 py-2 border border-border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => openTemplateManager('offboarding')}
          >
            <Settings className="w-4 h-4 me-2" />
            {t('Manage Templates')}
          </button>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600"
            onClick={() => setShowStartOffboarding(true)}
          >
            <Plus className="w-4 h-4 me-2" />
            {t('Start Offboarding')}
          </button>
        </div>
      </div>

      {/* Loading / Error / Empty States */}
      {offboardingLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-6 h-6 animate-spin text-red-600 me-2" />
          <span className="text-gray-600">{t('Loading offboarding processes...')}</span>
        </div>
      )}

      {offboardingError && !offboardingLoading && (
        <div className="text-center py-12">
          <p className="text-red-600 mb-2">{offboardingError}</p>
          <button onClick={refreshOffboarding} className="text-blue-600 hover:underline text-sm">{t('Retry')}</button>
        </div>
      )}

      {!offboardingLoading && !offboardingError && offboardingProcesses.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <UserMinus className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t('No offboarding processes yet. Click "Start Offboarding" to begin.')}</p>
        </div>
      )}

      {/* List View */}
      {!offboardingLoading && !offboardingError && offboardingViewType === 'list' && offboardingProcesses.length > 0 && (
        <div className="space-y-6">
          {offboardingProcesses.map((process) => {
            const statusLabel = getOffboardingStatusLabel(process.status);
            return (
            <div key={process.id} className="bg-card border border-border rounded-lg p-6 hover:border-border transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{process.employeeName}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(statusLabel)}`}>
                      {t(statusLabel)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">{t('Position')}:</span>
                      <div className="font-medium">{process.position || t('N/A')}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('Department')}:</span>
                      <div className="font-medium">{process.department}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('Last Working Day')}:</span>
                      <div className="font-medium">{appDate(process.lastWorkingDay)}</div>
                    </div>
                  </div>
                </div>
                <div className="text-end ms-6">
                  <div className="text-sm text-gray-500 mb-1">{t('Progress')}</div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-red-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${process.completionPercentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{Math.round(process.completionPercentage)}%</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center space-x-4 text-sm">
                  <div className="text-gray-600">
                    {t('Reason')}: <span className="font-medium">{process.exitReasonName ? t(process.exitReasonName) : '-'}</span>
                  </div>
                  {process.eosAmount != null && (
                    <div className="text-gray-600">
                      {t('EOS')}: <span className="font-medium text-amber-700">{process.currencyCode || 'AED'} {process.eosAmount.toLocaleString()}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => { setOffboardingKanbanProcess({ tasks: mapOffboardingToKanbanTasks(process) }); setOffboardingViewType('kanban'); }}
                  >
                    <LayoutGrid className="w-3.5 h-3.5 me-1" />
                    {t('Kanban')}
                  </button>
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => { setSelectedProcess(process); setShowViewOffboarding(true); }}
                  >
                    <Eye className="w-3.5 h-3.5 me-1" />
                    {t('View Details')}
                  </button>
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => generateOffboardingReport(process)}
                  >
                    <FileText className="w-3.5 h-3.5 me-1" />
                    {t('Generate Report')}
                  </button>
                  {process.status !== OffboardingStatus.Cancelled && process.status !== OffboardingStatus.Completed && (
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                      onClick={() => { setSelectedProcess(process); setShowEditOffboarding(true); }}
                    >
                      <Edit className="w-3.5 h-3.5 me-1" />
                      {t('Edit')}
                    </button>
                  )}
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={async () => {
                      console.log('[EOS] Calculate button clicked for process:', {
                        processId: process.id,
                        employeeName: process.employeeName,
                        userId: process.userId,
                        exitReasonName: process.exitReasonName,
                        lastWorkingDay: process.lastWorkingDay,
                        currentEosAmount: process.eosAmount,
                        currencyCode: process.currencyCode,
                        status: process.status,
                      });
                      try {
                        console.log('[EOS] Calling POST /api/OffboardingProcess/CalculateEos/' + process.id);
                        const result = await calculateEos(process.id);
                        console.log('[EOS] Raw API response:', result);
                        if (result.errors && result.errors.length > 0) {
                          console.error('[EOS] Calculation errors:', result.errors);
                          // `description` is the field the API populates; the previous `message` read
                          // was always undefined, so every server explanation collapsed to the generic
                          // fallback.
                          showToast(result.errors[0]?.description || t('EOS calculation failed'), 'error');
                        } else {
                          console.log('[EOS] Success. Updated process:', {
                            eosAmount: result.payload?.eosAmount,
                            currencyCode: result.payload?.currencyCode,
                            status: result.payload?.status,
                          });
                          showToast(t('End of service calculated'), 'success');
                          refreshOffboarding();
                        }
                      } catch (err) {
                        console.error('[EOS] Exception thrown:', err);
                        const thrown = err as {
                          errors?: { description?: string }[];
                          response?: { data?: { errors?: { description?: string }[] } };
                        };
                        const apiError = thrown?.errors?.[0]?.description
                          || thrown?.response?.data?.errors?.[0]?.description;
                        showToast(apiError ?? t('EOS calculation failed. Please check payroll settings.'), 'error');
                      }
                    }}
                  >
                    <Calculator className="w-3.5 h-3.5 me-1" />
                    {t('EOS')}
                  </button>
                  {process.status !== OffboardingStatus.Cancelled && process.status !== OffboardingStatus.Completed && (
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-orange-700 text-sm font-medium border border-orange-300 rounded-md"
                      onClick={() => {
                        setConfirmationConfig({
                          title: t('Cancel Offboarding Process'),
                          message: t('Are you sure you want to cancel the offboarding process for') + ` ${process.employeeName}?`,
                          confirmText: t('Cancel Process'),
                          onConfirm: () => runLifecycleAction(
                            () => cancelOffboarding(process.id),
                            {
                              failureMessage: t('Failed to cancel offboarding process'),
                              onSuccess: refreshOffboarding,
                              onSettled: () => setShowConfirmation(false),
                            },
                          )
                        });
                        setShowConfirmation(true);
                      }}
                    >
                      <XCircle className="w-3.5 h-3.5 me-1" />
                      {t('Cancel')}
                    </button>
                  )}
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-gray-700 text-sm font-medium border border-border rounded-md"
                    onClick={() => {
                      setDeleteConfig({
                        title: t('Delete Offboarding Process'),
                        message: t('Are you sure you want to delete the offboarding process for') + ` ${process.employeeName}?`,
                        confirmText: t('Delete Process'),
                        onConfirm: () => runLifecycleAction(
                          () => deleteOffboarding(process.id),
                          {
                            failureMessage: t('Failed to delete offboarding process'),
                            onSuccess: refreshOffboarding,
                            onSettled: () => setShowDeleteConfirmation(false),
                          },
                        )
                      });
                      setShowDeleteConfirmation(true);
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5 me-1" />
                    {t('Delete')}
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Kanban View */}
      {offboardingViewType === 'kanban' && offboardingKanbanProcess && (
        <div>
          <div className="mb-4">
            <button
              onClick={() => { setOffboardingViewType('list'); setOffboardingKanbanProcess(null); }}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-border rounded-md hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 me-2" />
              {t('Back to List View')}
            </button>
          </div>
          <KanbanBoard tasks={offboardingKanbanProcess.tasks} t={t} />
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('Employment Lifecycle')}</h1>
            <p className="text-gray-600 mt-2">
              {t('Manage employee journey from onboarding to offboarding')}
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            {activeTab === 'onboarding' && <UserPlus className="w-4 h-4" />}
            {activeTab === 'internal-movements' && <ArrowRightLeft className="w-4 h-4" />}
            {activeTab === 'offboarding' && <UserMinus className="w-4 h-4" />}
            <span>
              {activeTab === 'onboarding' && t('{{count}} active process', { count: onboardingProcesses.length })}
              {activeTab === 'internal-movements' && t('{{count}} pending movement', { count: internalMovements.length })}
              {activeTab === 'offboarding' && t('{{count}} exit process', { count: offboardingProcesses.length })}
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-card rounded-lg shadow-sm border border-border mb-6">
        <div className="border-b border-border">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-border'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200`}
              >
                {tab.icon}
                <span>{t(tab.label)}</span>
                <span className={`${
                  activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                } inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'onboarding' && renderOnboardingTab()}
        {activeTab === 'internal-movements' && renderInternalMovementsTab()}
        {activeTab === 'offboarding' && renderOffboardingTab()}
      </div>

      {/* Modals */}
      <StartOnboardingModal
        isOpen={showStartOnboarding}
        onClose={() => setShowStartOnboarding(false)}
        onSubmit={() => {
          setShowStartOnboarding(false);
          refreshOnboarding();
        }}
        templates={onboardingTemplates}
      />

      <StartOffboardingModal
        isOpen={showStartOffboarding}
        onClose={() => setShowStartOffboarding(false)}
        onSubmit={() => {
          setShowStartOffboarding(false);
          refreshOffboarding();
        }}
        templates={offboardingTemplates}
      />

      <InitiateMovementModal
        isOpen={showInitiateMovement}
        onClose={() => setShowInitiateMovement(false)}
        onSubmit={() => {
          setShowInitiateMovement(false);
          refreshMovements();
        }}
        templates={movementTemplates}
      />

      <EditOnboardingModal
        isOpen={showEditOnboarding}
        onClose={() => { setShowEditOnboarding(false); setSelectedProcess(null); refreshOnboarding(); }}
        onSubmit={() => {
          setShowEditOnboarding(false);
          setSelectedProcess(null);
          refreshOnboarding();
        }}
        process={selectedProcess && 'userId' in selectedProcess ? selectedProcess as OnboardingProcessView : null}
      />

      <EditOffboardingModal
        isOpen={showEditOffboarding}
        onClose={() => { setShowEditOffboarding(false); setSelectedProcess(null); refreshOffboarding(); }}
        onSubmit={() => {
          setShowEditOffboarding(false);
          setSelectedProcess(null);
          refreshOffboarding();
        }}
        process={selectedProcess && 'exitReasonId' in selectedProcess ? selectedProcess as OffboardingProcessView : null}
      />

      <EditMovementModal
        isOpen={showEditMovement}
        onClose={() => { setShowEditMovement(false); setSelectedMovement(null); refreshMovements(); }}
        onSubmit={() => {
          setShowEditMovement(false);
          setSelectedMovement(null);
          refreshMovements();
        }}
        movement={selectedMovement}
      />

      <OnboardingDetailsModal
        isOpen={showViewOnboarding}
        onClose={() => { setShowViewOnboarding(false); setSelectedProcess(null); refreshOnboarding(); }}
        process={selectedProcess && 'userId' in selectedProcess ? selectedProcess as OnboardingProcessView : null}
      />

      <OffboardingDetailsModal
        isOpen={showViewOffboarding}
        onClose={() => { setShowViewOffboarding(false); setSelectedProcess(null); refreshOffboarding(); }}
        process={selectedProcess && 'exitReasonId' in selectedProcess ? selectedProcess as OffboardingProcessView : null}
      />

      <MovementDetailsModal
        isOpen={showViewMovement}
        onClose={() => { setShowViewMovement(false); setSelectedMovement(null); refreshMovements(); }}
        movement={selectedMovement}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => { setShowDeleteConfirmation(false); setDeleteConfig({}); }}
        {...deleteConfig}
      />

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => { setShowConfirmation(false); setConfirmationConfig({}); }}
        {...confirmationConfig}
      />

      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('Offboarding Report')}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => reportIframeRef.current?.contentWindow?.print()}
                  className="inline-flex items-center px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90"
                >
                  <Printer className="w-4 h-4 me-2" />
                  {t('Print')}
                </button>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-600 ms-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <iframe
              ref={reportIframeRef}
              srcDoc={reportHtml}
              className="flex-1 w-full border-0 rounded-b-lg"
              title={t('Offboarding Report')}
            />
          </div>
        </div>
      )}

      <TemplateManagerPanel
        isOpen={showTemplateManager}
        onClose={() => {
          setShowTemplateManager(false);
          if (templateCategory === 0) loadOnboardingTemplates();
          else if (templateCategory === 1) loadOffboardingTemplates();
          else loadMovementTemplates();
        }}
        category={templateCategory}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />

    </div>
  );
}
